import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAdminGroupsReport } from '../../api/adminGroupsApi';
import { adminDashboardErrorMessage } from '../../utils/adminDashboardAuth';
import { adminDiag } from '../../utils/adminDiagnostics';
import Button from '../ui/Button';
import Alert from '../ui/Alert';
import Card from '../ui/Card';
import { typography } from '../../design-system/tokens';

function MetricRow({ label, value, hint }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-gray-800/80 last:border-0">
      <div>
        <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{label}</span>
        {hint && <p className="text-[10px] text-gray-600 font-medium mt-0.5">{hint}</p>}
      </div>
      <span className="text-sm font-black tabular-nums text-gray-200">{value}</span>
    </div>
  );
}

export default function AdminGroupsDashboard({ userEmail }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminDiag('admin_dashboard_rendered');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminGroupsReport();
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(adminDashboardErrorMessage(err, 'Could not load groups report'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const topGroups = report?.topActiveGroups || [];
  const topLinkGroups = report?.topGroupsByLinkJoins || [];
  const joinSplit = report?.joinsViaLinkVsNamed;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white px-4 py-8 sm:px-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`${typography.microWide} text-violet-400 mb-1`}>LockedIn Groups</p>
            <h1 className="text-2xl font-black tracking-tight">Leagues dashboard</h1>
            <p className="text-xs text-gray-500 mt-1">
              Read-only · {userEmail || 'signed in'}
              {report?.generatedAt ? ` · ${new Date(report.generatedAt).toLocaleString()}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/admin/finance" className="text-xs font-bold text-violet-400 hover:text-violet-300">
              Finance dashboard
            </Link>
            <Button type="button" variant="admin" size="sm" disabled={loading} onClick={load}>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
        </header>

        {error && (
          <Alert tone="error" className="text-sm font-bold">{error}</Alert>
        )}

        {report && (
          <>
            <Card variant="admin" className="p-4">
              <p className={`${typography.microWide} text-gray-500 mb-2`}>Group inventory</p>
              <MetricRow label="Total groups" value={report.totalGroups ?? '—'} />
              <MetricRow
                label="Groups with 1 member"
                value={report.groupsWithOneMember ?? '—'}
                hint="Activation gap — owner only, no rival yet"
              />
              <MetricRow
                label="Groups with 2+ members"
                value={report.groupsWithTwoPlusMembers ?? '—'}
                hint="Ready for standings competition"
              />
              <MetricRow
                label="Active groups (7d)"
                value={report.activeGroups7d ?? '—'}
                hint="Activity from locks or feed events in last 7 days"
              />
              <MetricRow label="Total members" value={report.totalMembers ?? '—'} />
              <MetricRow label="Avg members per group" value={report.avgMembersPerGroup ?? '—'} />
            </Card>

            <Card variant="admin" className="p-4">
              <p className={`${typography.microWide} text-gray-500 mb-2`}>Invites</p>
              <MetricRow label="Invites sent" value={report.invitesSent ?? '—'} />
              <MetricRow label="Invites accepted" value={report.invitesAccepted ?? '—'} />
              <MetricRow label="Invites declined" value={report.invitesDeclined ?? '—'} />
            </Card>

            <Card variant="admin" className="p-4 border-violet-500/20">
              <p className={`${typography.microWide} text-violet-300 mb-2`}>Invite links (growth loop)</p>
              <MetricRow
                label="Links created (total)"
                value={report.inviteLinksCreatedTotal ?? '—'}
                hint="adminMetrics.linksCreatedTotal or groupInviteLinks count"
              />
              <MetricRow
                label="Active links"
                value={report.inviteLinksActive ?? '—'}
                hint="status active, not expired/exhausted"
              />
              <MetricRow
                label="Link joins (total)"
                value={report.linkJoinsTotal ?? '—'}
                hint="adminMetrics.linkJoinsTotal or members with joinSource=link"
              />
              <MetricRow
                label="Link joins (7d)"
                value={report.linkJoins7d ?? '—'}
                hint="members joined via link in last 7 days"
              />
              <MetricRow
                label="Joins via link"
                value={joinSplit?.linkJoins ?? '—'}
                hint="groups/{id}/members joinSource=link"
              />
              <MetricRow
                label="Joins via named invite"
                value={joinSplit?.namedInviteJoins ?? '—'}
                hint="role=member without link joinSource"
              />
              <MetricRow
                label="Link join share"
                value={joinSplit?.linkJoinShare != null ? `${joinSplit.linkJoinShare}%` : '—'}
              />
              <MetricRow label="Revoked links" value={report.revokedLinks ?? '—'} />
              <MetricRow label="Expired links" value={report.expiredLinks ?? '—'} />
              <MetricRow label="Exhausted links" value={report.exhaustedLinks ?? '—'} />
            </Card>

            <Card variant="admin" className="p-4">
              <p className={`${typography.microWide} text-gray-500 mb-2`}>Group locks</p>
              <MetricRow
                label="Open group locks"
                value={report.groupLocksPostedOpen ?? '—'}
                hint="open_challenges with groupId, status open"
              />
              <MetricRow
                label="Accepted group locks"
                value={report.groupLocksAccepted ?? '—'}
                hint="open_challenges with groupId, status matched"
              />
            </Card>

            <Card variant="admin" className="p-4">
              <p className={`${typography.microWide} text-gray-500 mb-2`}>Free tier cap</p>
              <MetricRow
                label="Users at 2-group cap (free)"
                value={report.usersAtFreeJoinCap ?? '—'}
                hint="Non-Plus users with ≥2 groupMemberships"
              />
              <MetricRow
                label="Join attempts blocked (total)"
                value={report.joinCapBlocksTotal ?? '—'}
                hint="accept_group_invite 403 cap rejections"
              />
            </Card>

            {report.plusUpgradeAttribution && (
              <Card variant="admin" className="p-4 border-gray-800">
                <p className={`${typography.microWide} text-gray-500 mb-2`}>Plus upgrades by source</p>
                {report.plusUpgradeAttribution.available ? (
                  <div className="space-y-1">
                    {Object.entries(report.plusUpgradeAttribution.upgradesBySource || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([source, count]) => (
                        <MetricRow key={source} label={source} value={count} />
                      ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 font-medium">
                    {report.plusUpgradeAttribution.note || 'Not available yet'}
                  </p>
                )}
              </Card>
            )}

            {topLinkGroups.length > 0 && (
              <Card variant="admin" className="p-4">
                <p className={`${typography.microWide} text-violet-300 mb-3`}>Top groups by link joins</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="text-gray-500 uppercase tracking-widest">
                        <th className="pb-2 pr-3 font-black">Group</th>
                        <th className="pb-2 pr-3 font-black">Link joins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topLinkGroups.map((row) => (
                        <tr key={row.groupId} className="border-t border-gray-800/60 text-gray-300">
                          <td className="py-2 pr-3 font-bold">{row.name || row.groupId}</td>
                          <td className="py-2 pr-3 font-mono">{row.linkJoins ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {topGroups.length > 0 && (
              <Card variant="admin" className="p-4">
                <p className={`${typography.microWide} text-violet-300 mb-3`}>Top active groups (7d)</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="text-gray-500 uppercase tracking-widest">
                        <th className="pb-2 pr-3 font-black">Group</th>
                        <th className="pb-2 pr-3 font-black">Members</th>
                        <th className="pb-2 pr-3 font-black">Activity 7d</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topGroups.map((row) => (
                        <tr key={row.groupId} className="border-t border-gray-800/60 text-gray-300">
                          <td className="py-2 pr-3 font-bold">{row.name || row.groupId}</td>
                          <td className="py-2 pr-3 font-mono">{row.memberCount ?? '—'}</td>
                          <td className="py-2 pr-3 font-mono">{row.activity7d ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        <p className="text-[10px] text-gray-600 text-center pb-8">
          Read-only metrics · no wallet, Stripe, or group mutations from this screen
        </p>
      </div>
    </div>
  );
}
